import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { ROLES } from '../services/permissionService'
import { useDevices } from '../hooks/useDevices'
import { useStockAlerts } from '../hooks/useStockAlerts'
import MessagesService from '../services/messagesService'
import { createTxnSummaryService } from '../services/txnSummaryService'
import CompanyLogo from './CompanyLogo'
import DeviceList from './DeviceList'
import LegacyDeviceList from './LegacyDeviceList'
import DiagnoseList from './DiagnoseList'
import Downloads from './Downloads'
import MFASetup from './MFASetup'
import AlertBadge from './AlertBadge'
import ProductMasterList from './ProductMasterList'
import Analytics from './Analytics'
import Reports from './Reports'
import UserManagement from './UserManagement'
import Help from './Help'
import VersionInfo from './VersionInfo'
import WhatsNewModal, { shouldShowWhatsNew } from './WhatsNewModal'

export default function Dashboard() {
  // ALL HOOKS MUST BE CALLED FIRST - NO CONDITIONAL LOGIC ALLOWED
  const auth = useAuth()
  const navigate = useNavigate()
  const { 
    devices, 
    devicesSummary, 
    recentActivity, 
    recentMessages,
    deviceStatuses,
    loading: devicesLoading, 
    error: devicesError,
    refreshData,
    customerName,
    userPermissions: deviceAccessSummary
  } = useDevices()
  
  // Enable stock alerts and device offline monitoring with user's custom thresholds
  useStockAlerts(devices, deviceStatuses, auth?.userPermissions?.alert_thresholds)
  
  const [activeTab, setActiveTab] = useState('dashboard')
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [showMFASetup, setShowMFASetup] = useState(false)
  const [deviceListFilter, setDeviceListFilter] = useState(null)
  
  const [liveDeviceData, setLiveDeviceData] = useState([])
  const [liveDevicePeriod, setLiveDevicePeriod] = useState('7d')
  const [liveDeviceLoading, setLiveDeviceLoading] = useState(false)
  const [liveDeviceError, setLiveDeviceError] = useState(null)
  const [hoveredBarInfo, setHoveredBarInfo] = useState(null)
  const [showWhatsNew, setShowWhatsNew] = useState(false)

  useEffect(() => {
    if (auth?.currentUser && shouldShowWhatsNew()) {
      setShowWhatsNew(true)
    }
  }, [auth?.currentUser])

  useEffect(() => {
    let isCurrent = true

    const fetchLiveDeviceData = async () => {
      if (!auth?.userClaims?.customer) return
      
      setLiveDeviceLoading(true)
      setLiveDeviceError(null)
      
      try {
        const txnService = createTxnSummaryService(auth.userClaims)
        const data = await txnService.getLiveDeviceCounts(liveDevicePeriod)
        if (isCurrent) {
          setLiveDeviceData(data)
        }
      } catch (error) {
        console.error('Error fetching live device data:', error)
        if (isCurrent) {
          setLiveDeviceError('Failed to load chart data')
        }
      } finally {
        if (isCurrent) {
          setLiveDeviceLoading(false)
        }
      }
    }
    
    fetchLiveDeviceData()

    return () => {
      isCurrent = false
    }
  }, [auth?.userClaims, liveDevicePeriod])

  // Redirect replenish-only workers to their landing page
  React.useEffect(() => {
    const userRoles = auth?.userPermissions?.roles || []
    const isReplenishOnly = userRoles.length === 1 && userRoles.includes('replenish_worker')
    if (isReplenishOnly) {
      console.log('Dashboard: Redirecting replenish-only worker to landing page')
      navigate('/replenish-worker', { replace: true })
    }
  }, [auth?.userPermissions, navigate])

  // For device_admin / reports_admin users (no customer_admin), skip the dashboard home tab
  React.useEffect(() => {
    if (!auth?.userPermissions || auth?.permissionsLoading) return
    const userRoles = auth?.userPermissions?.roles || []
    const isSadmin = auth?.userClaims?.sadmin
    const hasCustomerAdmin = userRoles.includes(ROLES.CUSTOMER_ADMIN)
    if (!isSadmin && !hasCustomerAdmin && activeTab === 'dashboard') {
      if (userRoles.includes(ROLES.DEVICE_ADMIN)) {
        setActiveTab('devices')
      } else if (userRoles.includes(ROLES.REPORTS_ADMIN)) {
        setActiveTab('reports')
      }
    }
  }, [auth?.userPermissions, auth?.permissionsLoading, auth?.userClaims])

  // Update active tab when user status changes - useEffect hook
  React.useEffect(() => {
    if (auth?.userPermissions?.status === 'suspended' || auth?.userPermissions?.status === 'inactive') {
      if (activeTab === 'dashboard') {
        setActiveTab('account-status')
      }
    }
  }, [auth?.userPermissions?.status, activeTab])

  // CONDITIONAL RENDERING LOGIC - NO EARLY RETURNS WITH HOOKS
  // Handle case where auth context is not ready
  if (!auth) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }
  
  // Destructure auth values safely after null check
  const { 
    currentUser, 
    userClaims, 
    logout, 
    getMFAStatus, 
    hasPermission, 
    PERMISSIONS, 
    permissionsLoading, 
    permissionsReady,
    permissionsError,
    userPermissions: authenticatedPermissions,
    forceRefreshPermissions 
  } = auth
  
  // Handle permissions loading
  if (permissionsLoading || !permissionsReady || !userClaims) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading permissions...</span>
        </div>
      </div>
    )
  }

  if (!userClaims.sadmin && !authenticatedPermissions) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-4">
        <div className="card border-0 shadow-sm text-center" style={{ maxWidth: '460px' }}>
          <div className="card-body p-4">
            <i className="fas fa-user-shield text-warning fa-3x mb-3"></i>
            <h1 className="h4">Your access could not be loaded</h1>
            <p className="text-muted mb-4">
              {permissionsError || 'Please check your connection and try again.'}
            </p>
            <button type="button" className="btn btn-primary" onClick={forceRefreshPermissions}>
              <i className="fas fa-sync-alt me-2"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Get MFA status safely
  const mfaStatus = currentUser ? getMFAStatus() : null

  // Check if user is suspended or inactive
  const isSuspended = auth.userPermissions?.status === 'suspended'
  const isInactive = auth.userPermissions?.status === 'inactive'

  // Determine whether the dashboard home tab should be shown
  const _userRoles = auth.userPermissions?.roles || []
  const showDashboardTab = auth.userClaims?.sadmin || _userRoles.includes(ROLES.CUSTOMER_ADMIN) ||
    (!_userRoles.includes(ROLES.DEVICE_ADMIN) && !_userRoles.includes(ROLES.REPORTS_ADMIN))

  async function handleLogout() {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  return (
    <div className="dashboard-container">
      {/* Top Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark shadow-sm" style={{ minHeight: '50px', padding: '0.5rem 0', zIndex: 1030, position: 'relative' }}>
        <div className="container-fluid">
          <div className="navbar-brand d-flex align-items-center">
            <button
              className="btn btn-outline-light me-3 d-md-inline-block"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{ border: 'none', padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}
            >
              <i className={`fas ${sidebarCollapsed ? 'fa-bars' : 'fa-times'}`}></i>
            </button>
            <CompanyLogo size="xs" className="me-2" />
          </div>
          
          <div className="navbar-nav ms-auto">
            <div className="nav-item dropdown">
              <button
                className="nav-link dropdown-toggle btn btn-link text-white d-flex align-items-center"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="fas fa-user-circle me-2"></i>
                {currentUser?.displayName || currentUser?.email}
              </button>
              <ul className="dropdown-menu dropdown-menu-end" style={{ zIndex: 1050 }}>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => {e.preventDefault(); setActiveTab('profile')}}>
                    <i className="fas fa-user me-2"></i>
                    Profile
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" href="#" onClick={(e) => {e.preventDefault(); setActiveTab('settings')}}>
                    <i className="fas fa-cog me-2"></i>
                    Settings
                  </a>
                </li>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt me-2"></i>
                    Sign Out
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <div className="container-fluid">
        <div className="row">
          {/* Sidebar */}
          <div className={`${sidebarCollapsed ? 'col-auto' : 'col-md-3 col-lg-2'} px-0 bg-light border-end min-vh-100 sidebar-container`}>
            <div className={`d-flex flex-column h-100 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
              {!sidebarCollapsed && <h6 className="text-muted text-uppercase mb-3">Menu</h6>}
              <nav className="nav flex-column">
                {!isSuspended && !isInactive && showDashboardTab && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                    onClick={() => setActiveTab('dashboard')}
                    title="Dashboard"
                  >
                    <i className="fas fa-tachometer-alt me-2"></i>
                    {!sidebarCollapsed && <span>Dashboard</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.DEVICES.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'devices' ? 'active' : ''}`}
                    onClick={() => setActiveTab('devices')}
                    title="Devices"
                  >
                    <i className="fas fa-desktop me-2"></i>
                    {!sidebarCollapsed && <span>Devices</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.DEVICES.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'legacy-devices' ? 'active' : ''}`}
                    onClick={() => setActiveTab('legacy-devices')}
                    title="Legacy Devices"
                  >
                    <i className="fas fa-server me-2"></i>
                    {!sidebarCollapsed && <span>Legacy Devices</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.DIAGNOSE.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'diagnose' ? 'active' : ''}`}
                    onClick={() => setActiveTab('diagnose')}
                    title="Diagnose"
                  >
                    <i className="fas fa-stethoscope me-2"></i>
                    {!sidebarCollapsed && <span>Diagnose</span>}
                    {!sidebarCollapsed && (
                      <span className="badge bg-info ms-2">
                        <i className="fas fa-circle me-1" style={{ fontSize: '0.6rem' }}></i>
                        Live
                      </span>
                    )}
                  </button>
                )}
                {/* Analytics temporarily hidden due to conflicts
                <button
                  className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'analytics' ? 'active' : ''}`}
                  onClick={() => setActiveTab('analytics')}
                  title="Analytics"
                >
                  <i className="fas fa-chart-line me-2"></i>
                  {!sidebarCollapsed && <span>Analytics</span>}
                </button>
                */}
                {hasPermission(PERMISSIONS.REPORTS.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'reports' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reports')}
                    title="Reports"
                  >
                    <i className="fas fa-file-alt me-2"></i>
                    {!sidebarCollapsed && <span>Reports</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.INVENTORY.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'inventory' ? 'active' : ''}`}
                    onClick={() => setActiveTab('inventory')}
                    title="Inventory"
                  >
                    <i className="fas fa-boxes me-2"></i>
                    {!sidebarCollapsed && <span>Inventory</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.USERS.VIEW) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'users' ? 'active' : ''}`}
                    onClick={() => setActiveTab('users')}
                    title="Users"
                  >
                    <i className="fas fa-users me-2"></i>
                    {!sidebarCollapsed && <span>Users</span>}
                  </button>
                )}
                {hasPermission(PERMISSIONS.DOWNLOADS.ACCESS) && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'downloads' ? 'active' : ''}`}
                    onClick={() => setActiveTab('downloads')}
                    title="Downloads"
                  >
                    <i className="fas fa-download me-2"></i>
                    {!sidebarCollapsed && <span>Downloads</span>}
                  </button>
                )}
                {!isSuspended && !isInactive && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'notifications' ? 'active' : ''}`}
                    onClick={() => navigate('/notifications')}
                    title="Notifications"
                  >
                    <div className="position-relative d-inline-block">
                      <i className="fas fa-bell me-2"></i>
                      <AlertBadge />
                    </div>
                    {!sidebarCollapsed && <span>Notifications</span>}
                  </button>
                )}
              </nav>
              
              {/* Super Admin Section */}
              {userClaims?.sadmin && (
                <>
                  {!sidebarCollapsed && <hr className="my-3" />}
                  {!sidebarCollapsed && (
                    <h6 className="text-muted text-uppercase mb-3">
                      <i className="fas fa-shield-alt me-2"></i>
                      Super Admin
                    </h6>
                  )}
                  <nav className="nav flex-column">
                    <button
                      className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'upi-qr' ? 'active' : ''}`}
                      onClick={() => navigate('/admin/upi-qr')}
                      title="UPI QR Generator"
                    >
                      <i className="fas fa-qrcode me-2"></i>
                      {!sidebarCollapsed && <span>UPI QR Generator</span>}
                    </button>
                    <button
                      className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'firebase-users' ? 'active' : ''}`}
                      onClick={() => navigate('/admin/firebase-users')}
                      title="Firebase Users"
                    >
                      <i className="fas fa-users-cog me-2"></i>
                      {!sidebarCollapsed && <span>Firebase Users</span>}
                    </button>
                    <button
                      className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'mqtt' ? 'active' : ''}`}
                      onClick={() => navigate('/admin/mqtt')}
                      title="MQTT Commands"
                    >
                      <i className="fas fa-broadcast-tower me-2"></i>
                      {!sidebarCollapsed && <span>MQTT</span>}
                    </button>
                  </nav>
                </>
              )}
              
              {!sidebarCollapsed && <hr className="my-3" />}
              
              {!sidebarCollapsed && <h6 className="text-muted text-uppercase mb-3">Account</h6>}
              <nav className="nav flex-column">
                <button
                  className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                  title="Profile"
                >
                  <i className="fas fa-user me-2"></i>
                  {!sidebarCollapsed && <span>Profile</span>}
                </button>
                <button
                  className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                  title="Settings"
                >
                  <i className="fas fa-cog me-2"></i>
                  {!sidebarCollapsed && <span>Settings</span>}
                </button>
                <button
                  className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'help' ? 'active' : ''}`}
                  onClick={() => setActiveTab('help')}
                  title="Help"
                >
                  <i className="fas fa-question-circle me-2"></i>
                  {!sidebarCollapsed && <span>Help</span>}
                </button>
                {deviceAccessSummary?.isAdmin && (
                  <button
                    className={`nav-link text-start border-0 bg-transparent sidebar-nav-link ${activeTab === 'debug' ? 'active' : ''}`}
                    onClick={() => setActiveTab('debug')}
                    title="Debug & Testing"
                  >
                    <i className="fas fa-bug me-2"></i>
                    {!sidebarCollapsed && <span>Debug</span>}
                  </button>
                )}
              </nav>
              
              {/* Version Info */}
              {!sidebarCollapsed && (
                <div className="mt-auto pt-3">
                  <VersionInfo />
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="col-md-9 col-lg-10">
            <div className="p-4">
              {/* Account Status Alerts */}
              {isSuspended && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="alert alert-warning border-0 rounded-3 shadow-sm">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <i className="fas fa-exclamation-triangle fa-2x text-warning"></i>
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold text-warning">Account Suspended</h5>
                            <p className="mb-0">Your account has been temporarily suspended. You have limited access to the system. Please contact your administrator for assistance.</p>
                          </div>
                        </div>
                        <div>
                          <button 
                            className="btn btn-outline-warning btn-sm"
                            onClick={forceRefreshPermissions}
                            disabled={permissionsLoading}
                            title="Check if your account status has been updated"
                          >
                            {permissionsLoading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-sync-alt"></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {isInactive && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="alert alert-secondary border-0 rounded-3 shadow-sm">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            <i className="fas fa-user-slash fa-2x text-secondary"></i>
                          </div>
                          <div>
                            <h5 className="mb-1 fw-bold text-secondary">Account Inactive</h5>
                            <p className="mb-0">Your account is currently inactive. Please contact your administrator to reactivate your access.</p>
                          </div>
                        </div>
                        <div>
                          <button 
                            className="btn btn-outline-secondary btn-sm"
                            onClick={forceRefreshPermissions}
                            disabled={permissionsLoading}
                            title="Check if your account status has been updated"
                          >
                            {permissionsLoading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-sync-alt"></i>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Suspended/Inactive User Content */}
              {(isSuspended || isInactive) && activeTab === 'account-status' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h1 className="h3 mb-0">Account Status</h1>
                      <p className="text-muted">Your account access information</p>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-12">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                          <div className="text-center py-5">
                            <i className={`fas ${isSuspended ? 'fa-exclamation-triangle' : 'fa-user-slash'} fa-4x ${isSuspended ? 'text-warning' : 'text-secondary'} mb-4`}></i>
                            <h4 className={isSuspended ? 'text-warning' : 'text-secondary'}>
                              {isSuspended ? 'Account Suspended' : 'Account Inactive'}
                            </h4>
                            <p className="text-muted mb-4">
                              {isSuspended 
                                ? 'Your account has been temporarily suspended and most features are unavailable.'
                                : 'Your account is currently inactive and requires reactivation to access features.'
                              }
                            </p>
                            <div className="alert alert-light">
                              <strong>What you can do:</strong>
                              <ul className="mb-0 mt-2 text-start">
                                <li>Update your profile information</li>
                                <li>Change your password</li>
                                <li>Contact your administrator for assistance</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'dashboard' && !isSuspended && !isInactive && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h1 className="h3 mb-0">Dashboard</h1>
                      <p className="text-muted">
                        Welcome back, {currentUser?.displayName || currentUser?.email}
                        {customerName && <span className="ms-2">| Customer: <strong>{customerName}</strong></span>}
                      </p>
                      {deviceAccessSummary?.isAdmin && (
                        <span className="badge bg-warning text-dark me-2">Super Admin</span>
                      )}
                      {deviceAccessSummary?.hasCustomerAccess && (
                        <span className="badge bg-info me-2">Customer Access</span>
                      )}
                      {deviceAccessSummary?.hasLayoutAccess && (
                        <span className="badge bg-secondary me-2">Layout Access</span>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <button 
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={refreshData}
                        disabled={devicesLoading}
                      >
                        <i className="fas fa-sync-alt me-1"></i>
                        Refresh
                      </button>
                      <span className="badge bg-success">
                        <i className="fas fa-circle me-1"></i>
                        Online
                      </span>
                    </div>
                  </div>

                  {devicesError && (
                    <div className="alert alert-warning mb-4">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      <strong>Notice:</strong> {devicesError}
                      <br />
                      <small>This may be normal if you don't have any devices configured yet or if Firebase custom claims are not set up.</small>
                    </div>
                  )}

                  {/* Stats Cards */}
                  <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="text-muted text-uppercase mb-1">Total Devices</h6>
                              <h3 className="mb-0">
                                {devicesLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  devicesSummary?.total || 0
                                )}
                              </h3>
                              {devicesSummary?.total > 0 && (
                                <small className="text-muted">
                                  Customer: {customerName}
                                </small>
                              )}
                            </div>
                            <div className="text-primary">
                              <i className="fas fa-desktop fa-2x"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="text-muted text-uppercase mb-1">Online Devices</h6>
                              <h3 className="mb-0">
                                {devicesLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  devicesSummary?.online || 0
                                )}
                              </h3>
                              {devicesSummary?.total > 0 && (
                                <small className="text-success">
                                  {Math.round((devicesSummary?.online / devicesSummary?.total) * 100)}% operational
                                </small>
                              )}
                            </div>
                            <div className="text-success">
                              <i className="fas fa-check-circle fa-2x"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="text-muted text-uppercase mb-1">Revenue</h6>
                              <h3 className="mb-0">
                                {devicesLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  formatCurrency(devicesSummary?.totalRevenue || 0)
                                )}
                              </h3>
                              {devicesSummary?.totalRevenue > 0 && (
                                <small className="text-success">
                                  From {devicesSummary?.total} devices
                                </small>
                              )}
                            </div>
                            <div className="text-info">
                              <i className="fas fa-rupee-sign fa-2x"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="text-muted text-uppercase mb-1">Offline Devices</h6>
                              <h3 className="mb-0">
                                {devicesLoading ? (
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                ) : (
                                  (devicesSummary?.offline || 0) + (devicesSummary?.maintenance || 0)
                                )}
                              </h3>
                              {devicesSummary?.offline > 0 || devicesSummary?.maintenance > 0 ? (
                                <small className="text-danger">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Needs attention
                                </small>
                              ) : (
                                <small className="text-success">
                                  All systems operational
                                </small>
                              )}
                            </div>
                            <div className="text-warning">
                              <i className="fas fa-exclamation-triangle fa-2x"></i>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Live Devices Chart */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <i className="fas fa-chart-line me-2 text-primary"></i>
                            Live Devices
                          </h5>
                          <div className="btn-group btn-group-sm" role="group">
                            {['7d', '30d', '90d'].map((period) => (
                              <button
                                key={period}
                                type="button"
                                className={`btn ${liveDevicePeriod === period ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setLiveDevicePeriod(period)}
                              >
                                {period.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="card-body">
                          {liveDeviceLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <div className="mt-2 text-muted">Loading chart data...</div>
                            </div>
                          ) : liveDeviceError ? (
                            <div className="text-center py-4 text-danger">
                              <i className="fas fa-exclamation-circle fa-2x mb-2"></i>
                              <div>{liveDeviceError}</div>
                            </div>
                          ) : liveDeviceData.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-chart-bar fa-2x mb-2"></i>
                              <div>No data available for this period</div>
                            </div>
                          ) : (() => {
                            const maxLive = Math.max(...liveDeviceData.map(d => d.live), 1)
                            return (
                              <div style={{ position: 'relative' }}>
                                <style>{`
                                  .live-chart-bar {
                                    background-color: var(--bs-primary);
                                    transition: transform 0.15s ease;
                                  }
                                  .live-chart-bar:hover {
                                    background-color: var(--bs-success) !important;
                                    transform: scaleY(1.05);
                                    transform-origin: bottom;
                                  }
                                `}</style>
                                <div className="d-flex align-items-end" style={{ height: '180px', gap: '2px' }}>
                                  {liveDeviceData.map((day, index) => {
                                    const heightPercent = (day.live / maxLive) * 100
                                    const barHeight = Math.max((heightPercent / 100) * 180, 4)
                                    return (
                                      <div 
                                        key={index} 
                                        className="d-flex flex-column align-items-center justify-content-end flex-grow-1"
                                        style={{ minWidth: 0, height: '100%' }}
                                      >
                                        <div 
                                          className="live-chart-bar rounded-top w-100"
                                          style={{ height: `${barHeight}px`, cursor: 'pointer' }}
                                          onMouseEnter={() => setHoveredBarInfo({ live: day.live, date: day.dateFormatted })}
                                          onMouseLeave={() => setHoveredBarInfo(null)}
                                        />
                                      </div>
                                    )
                                  })}
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-2">
                                  <span className="text-muted small">{liveDeviceData[0]?.dateFormatted}</span>
                                  <span className="small">
                                    {hoveredBarInfo ? (
                                      <>
                                        <span className="fw-bold text-primary">{hoveredBarInfo.live}</span>
                                        <span className="text-muted"> devices on </span>
                                        <span className="fw-bold">{hoveredBarInfo.date}</span>
                                      </>
                                    ) : (
                                      <span className="text-muted">Hover over bars to see details</span>
                                    )}
                                  </span>
                                  <span className="text-muted small">{liveDeviceData[liveDeviceData.length - 1]?.dateFormatted}</span>
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Bypass Alert Card */}
                  {devices && devices.filter(d => d.enable_purchase_button === true).length > 0 && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div 
                          className="card border-danger shadow-sm cursor-pointer" 
                          style={{ cursor: 'pointer', borderWidth: '2px' }}
                          onClick={() => {
                            setDeviceListFilter({ paymentBypass: 'bypass-enabled' })
                            setActiveTab('devices')
                          }}
                          title="Click to view devices with payment bypass enabled"
                        >
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="text-danger me-3">
                                  <i className="fas fa-unlock-alt fa-3x"></i>
                                </div>
                                <div>
                                  <h5 className="mb-1 text-danger">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Payment Bypass Enabled
                                  </h5>
                                  <h3 className="mb-0">
                                    {devices.filter(d => d.enable_purchase_button === true).length} 
                                    {devices.filter(d => d.enable_purchase_button === true).length === 1 ? ' device' : ' devices'}
                                  </h3>
                                  <small className="text-muted">
                                    Free purchases currently allowed - Click to view and manage
                                  </small>
                                </div>
                              </div>
                              <div>
                                <button className="btn btn-danger">
                                  <i className="fas fa-eye me-2"></i>
                                  View Devices
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-8">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">Recent Activity</h5>
                          <small className="text-muted">
                            {recentMessages && recentMessages.length > 0 && (
                              `${recentMessages.length} message${recentMessages.length > 1 ? 's' : ''}`
                            )}
                          </small>
                        </div>
                        <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {devicesLoading ? (
                            <div className="text-center py-4">
                              <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <div className="mt-2">Loading recent activity...</div>
                            </div>
                          ) : recentMessages && recentMessages.length > 0 ? (
                            <div className="list-group list-group-flush">
                              {recentMessages.map((message, index) => (
                                <div key={message.id || index} className="list-group-item border-0 px-0 py-3">
                                  <div className="d-flex align-items-start">
                                    <div className="me-3">
                                      <i className={MessagesService.getActionIcon(message.type)}></i>
                                    </div>
                                    <div className="flex-grow-1">
                                      <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                          <h6 className="mb-1 fw-semibold">{message.type}</h6>
                                          <div className="d-flex align-items-center text-muted small mb-2">
                                            <span className={MessagesService.getSourceColor(message.source)}>
                                              <i className="fas fa-circle me-1" style={{ fontSize: '0.5rem' }}></i>
                                              {message.source}
                                            </span>
                                            {message.deviceId && (
                                              <>
                                                <span className="mx-2">•</span>
                                                <i className="fas fa-desktop me-1"></i>
                                                <span>{message.deviceId}</span>
                                                {(() => {
                                                  const device = devices?.find(d => d.id === message.deviceId)
                                                  return device?.location ? (
                                                    <>
                                                      <span className="mx-2">•</span>
                                                      <i className="fas fa-map-marker-alt me-1"></i>
                                                      <span>{device.location}</span>
                                                    </>
                                                  ) : null
                                                })()}
                                              </>
                                            )}
                                            {message.source === 'smart-cards' && message.cardId && (
                                              <>
                                                <span className="mx-2">•</span>
                                                <i className="fas fa-credit-card me-1"></i>
                                                <span>Card: {message.cardId}</span>
                                              </>
                                            )}
                                            {message.userEmail && (
                                              <>
                                                <span className="mx-2">•</span>
                                                <i className="fas fa-user me-1"></i>
                                                <span>{message.userEmail}</span>
                                              </>
                                            )}
                                          </div>
                                          <small className="text-muted">
                                            <i className="fas fa-clock me-1"></i>
                                            {MessagesService.formatRelativeTime(message.dateTime)}
                                          </small>
                                          <br />
                                          <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            <i className="fas fa-calendar me-1"></i>
                                            {MessagesService.formatActualTime(message.dateTime)}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-history fa-2x mb-3 d-block"></i>
                              {customerName ? 
                                `No recent activity found for customer "${customerName}". Activity messages will appear here once operations begin.` :
                                'No customer assigned. Please check your Firebase authentication claims.'
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0">
                          <h5 className="mb-0">Quick Actions</h5>
                        </div>
                        <div className="card-body">
                          <div className="d-grid gap-2">
                            <button 
                              className="btn btn-outline-primary"
                              onClick={() => setActiveTab('devices')}
                            >
                              <i className="fas fa-cog me-2"></i>
                              Manage Devices
                            </button>
                            <button className="btn btn-outline-primary">
                              <i className="fas fa-download me-2"></i>
                              Export Reports
                            </button>
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={refreshData}
                            >
                              <i className="fas fa-sync me-2"></i>
                              Sync All Devices
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'devices' && (
                <DeviceList 
                  onRefresh={refreshData} 
                  initialFilter={deviceListFilter}
                  onFilterApplied={() => setDeviceListFilter(null)}
                />
              )}

              {activeTab === 'legacy-devices' && (
                <LegacyDeviceList />
              )}

              {activeTab === 'diagnose' && (
                <DiagnoseList />
              )}

              {activeTab === 'downloads' && (
                <Downloads />
              )}

              {activeTab === 'analytics' && (
                <Analytics />
              )}

              {activeTab === 'inventory' && (
                <ProductMasterList />
              )}

              {activeTab === 'users' && (
                <UserManagement />
              )}

              {activeTab === 'reports' && (
                <Reports />
              )}

              {activeTab === 'help' && (
                <Help />
              )}

              {activeTab === 'profile' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">Profile</h1>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0">
                          <h5 className="mb-0">Account Information</h5>
                        </div>
                        <div className="card-body">
                          <div className="mb-3">
                            <label className="form-label">Display Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={currentUser?.displayName || ''} 
                              readOnly 
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input 
                              type="email" 
                              className="form-control" 
                              value={currentUser?.email || ''} 
                              readOnly 
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Email Verification</label>
                            <div>
                              {currentUser?.emailVerified ? (
                                <span className="badge bg-success">
                                  <i className="fas fa-check me-1"></i>
                                  Verified
                                </span>
                              ) : (
                                <span className="badge bg-warning">
                                  <i className="fas fa-exclamation-triangle me-1"></i>
                                  Not Verified
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Account Created</label>
                            <p className="mb-0">
                              {new Date(currentUser?.metadata?.creationTime).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">Settings</h1>
                  </div>
                  
                  {/* MFA Settings */}
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-header bg-white">
                      <h6 className="mb-0">
                        <i className="fas fa-shield-alt me-2"></i>
                        Multi-Factor Authentication
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col">
                          <h6 className="mb-1">SMS Authentication</h6>
                          <p className="text-muted mb-0">
                            {mfaStatus?.isEnabled ? (
                              <>
                                <span className="badge bg-success me-2">Enabled</span>
                                Your account is protected with SMS multi-factor authentication.
                                <br />
                                <small>
                                  Enrolled factors: {mfaStatus.enrolledFactors.length}
                                </small>
                              </>
                            ) : (
                              <>
                                <span className="badge bg-warning me-2">Disabled</span>
                                Add an extra layer of security to your account.
                              </>
                            )}
                          </p>
                        </div>
                        <div className="col-auto">
                          {mfaStatus?.isEnabled ? (
                            <button className="btn btn-outline-danger btn-sm" disabled>
                              <i className="fas fa-times me-2"></i>
                              Disable MFA
                            </button>
                          ) : (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => setShowMFASetup(true)}
                            >
                              <i className="fas fa-plus me-2"></i>
                              Enable MFA
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* User Profile */}
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white">
                      <h6 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        User Profile
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input 
                              type="email" 
                              className="form-control" 
                              value={currentUser?.email || ''} 
                              disabled 
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Display Name</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={currentUser?.displayName || ''} 
                              disabled 
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Customer</label>
                            <input 
                              type="text" 
                              className="form-control" 
                              value={customerName || 'Not assigned'} 
                              disabled 
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Permissions</label>
                            <div>
                              {deviceAccessSummary?.isAdmin && (
                                <span className="badge bg-warning text-dark me-2">Super Admin</span>
                              )}
                              {deviceAccessSummary?.hasCustomerAccess && (
                                <span className="badge bg-info me-2">Customer Access</span>
                              )}
                              {deviceAccessSummary?.hasLayoutAccess && (
                                <span className="badge bg-secondary me-2">Layout Access</span>
                              )}
                              {!deviceAccessSummary?.isAdmin && !deviceAccessSummary?.hasCustomerAccess && !deviceAccessSummary?.hasLayoutAccess && (
                                <span className="badge bg-light text-dark">No special permissions</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'debug' && deviceAccessSummary?.isAdmin && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h1 className="h3 mb-0">Debug & Testing</h1>
                  </div>
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <h5>Notification Testing</h5>
                          <p className="text-muted">Test the notification system to ensure alerts are working</p>
                          <button 
                            className="btn btn-warning me-2 mb-2"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('testNotification', {
                                detail: {
                                  title: 'Test Stock Alert',
                                  message: 'This is a test critical stock notification',
                                  priority: 'critical',
                                  category: 'stockAlerts'
                                }
                              }))
                            }}
                          >
                            Test Stock Alert
                          </button>
                          <button 
                            className="btn btn-info me-2 mb-2"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('testNotification', {
                                detail: {
                                  title: 'Test Device Status',
                                  message: 'This is a test device offline notification',
                                  priority: 'warning',
                                  category: 'deviceOffline'
                                }
                              }))
                            }}
                          >
                            Test Device Alert
                          </button>
                          <button 
                            className="btn btn-success me-2 mb-2"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('testNotification', {
                                detail: {
                                  title: 'Test System Message',
                                  message: 'This is a test system notification',
                                  priority: 'info',
                                  category: 'system'
                                }
                              }))
                            }}
                          >
                            Test System Alert
                          </button>
                          <button 
                            className="btn btn-danger me-2 mb-2"
                            onClick={() => {
                              // Clear session storage to reset notifications
                              sessionStorage.removeItem('monitoring_welcome_shown')
                              window.location.reload()
                            }}
                          >
                            Reset Monitoring
                          </button>
                        </div>
                        <div className="col-md-6">
                          <h5>Monitoring Status</h5>
                          <p className="text-muted">Check the status of global monitoring components</p>
                          <div className="alert alert-info">
                            <strong>Devices Found:</strong> {devices.length}<br/>
                            <strong>Last Activity:</strong> {recentActivity.length > 0 ? recentActivity[0]?.timestamp : 'None'}<br/>
                            <strong>Customer:</strong> {customerName || 'Not set'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MFA Setup Modal */}
      {showMFASetup && (
        <MFASetup
          onClose={() => setShowMFASetup(false)}
          onMFAEnabled={() => {
            setShowMFASetup(false)
            // Refresh the page to update MFA status
            window.location.reload()
          }}
        />
      )}

      {/* What's New Modal */}
      <WhatsNewModal 
        show={showWhatsNew} 
        onClose={() => setShowWhatsNew(false)} 
      />
    </div>
  )
}
